import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tiptapDocToPlainText } from '@odo/shared-ui';
import { useCreateCommunication } from '../../../../hooks/useCommunications';
import type {
    CommunicationChannel,
    CreateCommunicationInput
} from '../../../../services/communications';
import { useToast } from '../../../../providers/ToastProvider';
import { useUser } from '../../../../providers/UserProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { channelDef } from './channels';
import {
    buildCreateCommunicationInput,
    buildInitialCommunicationFormValues,
    communicationFormValuesDiffer,
    type CommunicationFormValues
} from './communicationFormValues';
import { MAX_ENTRY_BODY_LENGTH } from './entryBodyLimit';
import { durationErrorFor, entryBodyWouldFailValidation } from './entryFormValues';
import { generateReferenceNo } from './generateReferenceNo';
import { useCallTimer } from './useCallTimer';

interface UseCommunicationFormArgs {
    practiceId: string | null;
    defaultContact: string | null;
    /** True only for the practice-less entry point (`/accounts/communications/new`). */
    practiceIsEditable: boolean;
}

function isConflictError(err: unknown): boolean {
    return (
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        (err as { response?: { status?: number } }).response?.status === 409
    );
}

/** Owns the create-communication form's state, dirty tracking, and save flow.
 *  Title is the only required field. On success, navigates to the new
 *  thread's detail page. */
export function useCommunicationForm({
    practiceId,
    defaultContact,
    practiceIsEditable
}: UseCommunicationFormArgs) {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { user } = useUser();
    const createCommunication = useCreateCommunication();
    const timer = useCallTimer();

    // Generated once, at the moment this form mounts - the caller can be read
    // the number before anything is saved.
    const initialValues = useRef<CommunicationFormValues>(
        buildInitialCommunicationFormValues(generateReferenceNo(user?.name ?? ''), defaultContact)
    );
    const [form, setForm] = useState<CommunicationFormValues>(initialValues.current);
    // Non-null while the discard-changes modal is up, holding the in-app
    // navigation (Cancel, a breadcrumb crumb) that's waiting on the user's
    // choice.
    const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);

    // Practice-less form only: the patients linked belong to the practice
    // picked when they were added. Switching practices makes that set stale.
    const prevPracticeIdRef = useRef(practiceId);
    useEffect(() => {
        if (!practiceIsEditable || prevPracticeIdRef.current === practiceId) return;
        prevPracticeIdRef.current = practiceId;
        setForm((f) => (f.patients.length > 0 ? { ...f, patients: [] } : f));
    }, [practiceId, practiceIsEditable]);

    // Practice-less form only: prefills the contact from the newly picked
    // practice, unless the user already hand-edited it.
    const lastAutoContact = useRef(defaultContact);
    useEffect(() => {
        if (!practiceIsEditable || defaultContact === lastAutoContact.current) return;
        if (defaultContact === null) return;
        const previousAuto = lastAutoContact.current;
        lastAutoContact.current = defaultContact;
        setForm((f) => {
            const userTyped =
                f.entry.contactDisplay !== '' && f.entry.contactDisplay !== previousAuto;
            return userTyped ? f : { ...f, entry: { ...f.entry, contactDisplay: defaultContact } };
        });
    }, [defaultContact, practiceIsEditable]);

    const isSaving = createCommunication.isPending;
    const plainBody = tiptapDocToPlainText(form.entry.doc).trim();
    const overLimit = plainBody.length > MAX_ENTRY_BODY_LENGTH;
    const missingBodyText = entryBodyWouldFailValidation(
        initialValues.current.entry.doc,
        form.entry.doc
    );
    const isDirty = communicationFormValuesDiffer(initialValues.current, form);
    const durationError = useMemo(
        () => durationErrorFor(form.entry.durationMinutesDisplay),
        [form.entry.durationMinutesDisplay]
    );

    const canSave =
        form.title.trim().length > 0 &&
        !durationError &&
        !overLimit &&
        !missingBodyText &&
        !isSaving;

    // Guards hard navigation (refresh/close/typed URL) while edits are pending.
    useEffect(() => {
        if (!isDirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isDirty]);

    // Where Cancel returns to - fixed by entry point, not the picked practice.
    const returnPath = practiceIsEditable
        ? '/accounts/communications'
        : `/accounts/practices/${practiceId as string}?tab=communications`;

    // Every in-page navigation (Cancel, breadcrumb, Recent communications
    // rows) routes through this so a dirty form is never silently abandoned.
    const guardedNavigate = useCallback(
        (path: string) => {
            if (!isDirty) {
                navigate(path);
                return;
            }
            setPendingNavigationPath(path);
        },
        [isDirty, navigate]
    );

    const handleCancel = useCallback(() => {
        guardedNavigate(returnPath);
    }, [guardedNavigate, returnPath]);

    const confirmDiscardNavigation = useCallback(() => {
        if (pendingNavigationPath) navigate(pendingNavigationPath);
        setPendingNavigationPath(null);
    }, [pendingNavigationPath, navigate]);

    const cancelDiscardNavigation = useCallback(() => {
        setPendingNavigationPath(null);
    }, []);

    const onToggleTimer = useCallback(() => {
        if (timer.isRunning) {
            const minutes = timer.stop();
            if (minutes !== null) {
                setForm((f) => ({
                    ...f,
                    entry: { ...f.entry, durationMinutesDisplay: String(minutes) }
                }));
            }
        } else {
            timer.start();
        }
    }, [timer]);

    const setChannel = useCallback((channel: CommunicationChannel) => {
        setForm((f) => {
            const def = channelDef(channel);
            const hadDirection = channelDef(f.entry.channel).hasDirection;
            return {
                ...f,
                entry: {
                    ...f.entry,
                    channel,
                    // Clear fields the new channel doesn't show; direction
                    // resets to a default instead (there's no "empty" state).
                    direction: !hadDirection && def.hasDirection ? 'incoming' : f.entry.direction,
                    durationMinutesDisplay: def.hasDuration ? f.entry.durationMinutesDisplay : '',
                    contactDisplay: def.contactLabel ? f.entry.contactDisplay : '',
                    contactNameDisplay: def.contactNameLabel ? f.entry.contactNameDisplay : ''
                }
            };
        });
    }, []);

    // A 409 means the reference collided - regenerate and retry once. Takes
    // `input` explicitly rather than re-reading `form`: state from the
    // `setForm` below hasn't landed yet, so re-reading `form` here would
    // resend the same collided referenceNo.
    const submitCreate = useCallback(
        (input: CreateCommunicationInput, isRetry: boolean) => {
            createCommunication.mutate(input, {
                onSuccess: (created) => {
                    showToast(`Communication logged · ${created.referenceNo}`, 'success');
                    const path = created.practiceId
                        ? `/accounts/practices/${created.practiceId}/communications/${created.id}`
                        : `/accounts/communications/${created.id}`;
                    navigate(path);
                },
                onError: (err) => {
                    if (!isRetry && isConflictError(err)) {
                        const retryReferenceNo = generateReferenceNo(user?.name ?? '');
                        setForm((f) => ({ ...f, referenceNo: retryReferenceNo }));
                        initialValues.current = {
                            ...initialValues.current,
                            referenceNo: retryReferenceNo
                        };
                        submitCreate({ ...input, referenceNo: retryReferenceNo }, true);
                        return;
                    }
                    showToast(apiErrorMessage(err, 'Failed to log communication'), 'error');
                }
            });
        },
        [createCommunication, showToast, navigate, user?.name]
    );

    const handleSave = useCallback(() => {
        if (!canSave) return;

        let effective = form;
        if (timer.isRunning) {
            const minutes = timer.stop();
            if (minutes !== null) {
                effective = {
                    ...form,
                    entry: { ...form.entry, durationMinutesDisplay: String(minutes) }
                };
                setForm(effective);
            }
        }

        const input = buildCreateCommunicationInput(practiceId, effective);
        submitCreate(input, false);
    }, [canSave, form, timer, practiceId, submitCreate]);

    return {
        form,
        setForm,
        setChannel,
        isDirty,
        isSaving,
        canSave,
        overLimit,
        missingBodyText,
        durationError,
        plainBodyLength: plainBody.length,
        timerIsRunning: timer.isRunning,
        timerElapsedLabel: timer.elapsedLabel,
        onToggleTimer,
        guardedNavigate,
        handleCancel,
        handleSave,
        showDiscardModal: pendingNavigationPath !== null,
        confirmDiscardNavigation,
        cancelDiscardNavigation
    };
}
